import { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * ArtemisChaseCamera - Renders to texture
 * Always parented to the Artemis group (which moves along the path).
 * Default: offset above/behind, looking at ship origin (chase view).
 * Follow mode: positioned at group origin, quaternion driven by Orion_Capsule's
 *   world rotation so it inherits the capsule's orientation while the group
 *   provides the world-space trajectory position.
 */
export function ArtemisChaseCamera({
  onTextureUpdate,
  followModeActive = false,
  orionCapsuleRef,
  opacityOverrideRef = null,
  starSizeScaleRef = null,
}) {
  const { gl, scene } = useThree()
  const cameraRef = useRef()
  const renderTargetRef = useRef()
  const groupRef = useRef()
  const isMountedRef = useRef(true)
  const timeoutIdRef = useRef(null)
  const followModeActiveRef = useRef(followModeActive)
  followModeActiveRef.current = followModeActive

  // Reusable objects — allocated once, never recreated
  const chaseCamOffset = useRef(new THREE.Vector3(-20.0, 10.0, -20.0))
  const chaseLookAtOffset = useRef(new THREE.Vector3(0, 0, 0.0))
  const lookAtWorldRef = useRef(new THREE.Vector3())
  const capsuleWorldQuatRef = useRef(new THREE.Quaternion())
  const groupWorldQuatRef = useRef(new THREE.Quaternion())
  const opacityOverrideRefRef = useRef(opacityOverrideRef)
  opacityOverrideRefRef.current = opacityOverrideRef
  const starSizeScaleRefRef = useRef(starSizeScaleRef)
  starSizeScaleRefRef.current = starSizeScaleRef

  // Create render target and camera once
  useEffect(() => {
    const renderTarget = new THREE.WebGLRenderTarget(320, 240, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      samples: 4,
      generateMipmaps: false,
      colorSpace: THREE.SRGBColorSpace,
      usage: gl.isWebGPURenderer ?
        THREE.TextureBinding | THREE.CopyDst | THREE.RenderAttachment :
        undefined
    })
    renderTargetRef.current = renderTarget

    const camera = new THREE.PerspectiveCamera(60, 320 / 240, 0.01, 2000)
    camera.layers.set(0)
    camera.layers.enable(4) // layer 4: chase-cam-only objects (sun sprite)
    cameraRef.current = camera

    if (onTextureUpdate) {
      onTextureUpdate({ renderTarget, texture: renderTarget.texture })
    }

    return () => { renderTarget.dispose() }
  }, [onTextureUpdate])

  // Add camera to group on mount — stays here always
  useEffect(() => {
    if (cameraRef.current && groupRef.current) {
      groupRef.current.add(cameraRef.current)
      return () => {
        if (groupRef.current && cameraRef.current) {
          groupRef.current.remove(cameraRef.current)
        }
      }
    }
  }, [])

  // Stable helpers — read overrides via ref so they never need recreating per frame
  const setChaseCamOpacityRef = useRef((v) => {
    const overrideMaterials = opacityOverrideRefRef.current?.current
    if (!Array.isArray(overrideMaterials)) return
    for (const mat of overrideMaterials) {
      if (mat?.userData?.opacityFadeUniform) mat.userData.opacityFadeUniform.value = v
    }
  })

  const saveOpacityRef = useRef(() => {
    const overrideMaterials = opacityOverrideRefRef.current?.current
    if (!Array.isArray(overrideMaterials)) return 1
    for (const mat of overrideMaterials) {
      if (mat?.userData?.opacityFadeUniform) return mat.userData.opacityFadeUniform.value
    }
    return 1
  })

  // Stable deferred-render callback — avoids per-frame closure allocation on the WebGPU path
  const deferredRenderRef = useRef(() => {
    if (!isMountedRef.current) return
    if (renderTargetRef.current && cameraRef.current) {
      const saved = saveOpacityRef.current()
      setChaseCamOpacityRef.current(1)
      // Shrink the stars to half size for the chase view only — they read as
      // oversized in the small framed feed. Restored right after the render.
      const starU = starSizeScaleRefRef.current?.current
      const savedStarScale = starU ? starU.value : null
      if (starU) starU.value = 0.5
      const orig = gl.getRenderTarget()
      gl.setRenderTarget(renderTargetRef.current)
      gl.render(scene, cameraRef.current)
      gl.setRenderTarget(orig)
      setChaseCamOpacityRef.current(saved)
      if (starU) starU.value = savedStarScale
    }
  })

  useFrame(() => {
    if (!cameraRef.current || !renderTargetRef.current || !groupRef.current) return
    if (!isMountedRef.current) return

    const camera = cameraRef.current

    if (followModeActiveRef.current) {
      const capsule = orionCapsuleRef?.current
      if (capsule) {
        camera.position.set(0, 0, 0)
        capsule.getWorldQuaternion(capsuleWorldQuatRef.current)
        groupRef.current.getWorldQuaternion(groupWorldQuatRef.current)
        groupWorldQuatRef.current.invert()
        camera.quaternion.copy(groupWorldQuatRef.current).multiply(capsuleWorldQuatRef.current)
        camera.rotateY(Math.PI)
      }
    } else {
      // Chase cam: fixed offset in group local space, look at group origin
      camera.position.copy(chaseCamOffset.current)
      lookAtWorldRef.current.copy(chaseLookAtOffset.current)
      groupRef.current.localToWorld(lookAtWorldRef.current)
      camera.lookAt(lookAtWorldRef.current)
    }

    // Render to texture — bracket with opacity override so Earth stays fully visible
    // in the chase cam view regardless of main camera distance
    if (gl.isWebGPURenderer) {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = setTimeout(deferredRenderRef.current, 0)
    } else {
      deferredRenderRef.current()
    }
  })

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }
      if (onTextureUpdate) onTextureUpdate(null)
      if (renderTargetRef.current) {
        renderTargetRef.current.dispose()
        renderTargetRef.current = null
      }
      if (cameraRef.current) {
        if (cameraRef.current.parent) cameraRef.current.parent.remove(cameraRef.current)
        cameraRef.current = null
      }
      chaseCamOffset.current = null
      chaseLookAtOffset.current = null
      lookAtWorldRef.current = null
      capsuleWorldQuatRef.current = null
      groupWorldQuatRef.current = null
      groupRef.current = null
    }
  }, [onTextureUpdate])

  return <group ref={groupRef} />
}
